/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, memo } from 'react';
import styled from 'styled-components';
import ListItem from '@mui/material/ListItem';

import { FixedSizeList } from 'react-window';
import { Chip, Divider, LinearProgress, Stack } from '@mui/material';
import { FactCheckOutlined, FindInPageOutlined, UploadFile } from '@mui/icons-material';
import { BucketFile, QueueFile } from './Sidebar';
import { DocumentEvent } from '@common/hooks/types';
import { useTheme } from '../../../../../../styles/theme';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Button } from '@common/Button';
import { useSelector } from 'react-redux';
import { State } from '@store/index';
import { Card } from '@common/Card';
import { TelnyxBucket } from '@requests/buckets';

const TOP_NAVBAR_HEIGHT = 61;
const HEADER_HEIGHT = 100;

const MAX_FILE_WIDTH = '15.5vw';
const MAX_FILE_WIDTH_LOADING = '14.5vw';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  gap: 1rem;
  height: calc(100vh - ${HEADER_HEIGHT + TOP_NAVBAR_HEIGHT}px - 16px);
  overflow: hidden;

  && ::-webkit-scrollbar {
    height: 4px;
    width: 4px;
    background: ${({ theme }) => theme.background.standard};
    border-left: 1px solid ${({ theme }) => theme.line.light};
  }

  && ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.text.subtext};
  }

  && ::-webkit-scrollbar-thumb:hover {
    background: black;
  }

  & .MuiListItem-root {
    padding-top: 12px;
    padding-bottom: 12px;
  }

  & .MuiListItemText-secondary {
    font-size: 14px !important;
  }

  #index {
    padding-top: 4px;
    font-size: 12px;
    font-family: 'PPFormula';
  }

  p {
    color: #00000099;
    font-size: 14px;
    font-family: 'Inter';
    margin-bottom: 4px;
  }
`;

const QueueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 8px;
  overflow: hidden;
  gap: 16px;
`;

const File = styled.div<{ loading?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;

  h1 {
    font-size: 13px;
    font-weight: 400;
    font-family: 'Inter';

    max-width: ${({ loading }) => (loading ? MAX_FILE_WIDTH_LOADING : MAX_FILE_WIDTH)};
    min-width: ${({ loading }) => (loading ? MAX_FILE_WIDTH_LOADING : MAX_FILE_WIDTH)};
    color: ${({ theme }) => theme.text.standard};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding-bottom: 4px;

  @media (max-width: 1500px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

interface RowProps {
  row: QueueFile;
  style: React.CSSProperties;
}

const Row: React.FC<RowProps> = memo(
  ({ row, style }) => (
    <div style={style}>
      <ListItem>
        <File loading={row?.loading}>
          <Stack direction='row' alignItems='center' spacing={1}>
            {row?.event === DocumentEvent.Embed && <FindInPageOutlined sx={{ fontSize: '18px', color: '#06a779' }} />}
            {(row?.event === DocumentEvent.Upload || row?.event === DocumentEvent.Loader) && (
              <UploadFile sx={{ fontSize: '18px', color: '#3434EF' }} />
            )}
            <h1>{row?.id}</h1>
          </Stack>
          {row?.loading && (
            <div className='loader'>
              <LinearProgress color={row?.event === DocumentEvent.Embed ? 'success' : 'primary'} />
            </div>
          )}
        </File>
      </ListItem>
      <Divider />
    </div>
  ),
  (prevProps: RowProps, nextProps: RowProps) => {
    // This function checks if props have changed; return true to SKIP re-render
    return prevProps?.row?.loading === nextProps?.row?.loading;
  }
);

Row.displayName = 'Row';

interface Props {
  data: QueueFile[];
  setTab: React.Dispatch<string>;
  files: BucketFile[];
  isQueueFetching: boolean;
  loader: any | null;
}

export const Queue: React.FC<Props> = ({ data, files, setTab, loader, isQueueFetching }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { service_id, bucketName } = useParams();

  const task_id = useSelector((state: State) => state.buckets.task_id);
  const bucketQueryKey = ['globalFiles', 'bucket', { service_id, bucket_name: bucketName }];
  const bucket = queryClient.getQueryData<TelnyxBucket>(bucketQueryKey);

  const [listHeight, setListHeight] = useState(0);

  const calculateHeight = () => {
    const windowHeight = window.innerHeight;
    // (header + tabs) - top nav - footer height
    return windowHeight - 98 - TOP_NAVBAR_HEIGHT;
  };

  useEffect(() => {
    const updateHeight = () => setListHeight(calculateHeight());

    updateHeight();

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const body = (
    <Legend>
      <Chip
        variant='outlined'
        color='primary'
        size='small'
        sx={{
          color: theme.telnyx.blue,
          borderColor: theme.telnyx.blue,
          padding: '3px 2px !important',
          svg: { marginLeft: `4px !important` },
        }}
        icon={<UploadFile sx={{ fontSize: '2ch !important' }} />}
        label='Uploading File'
      />
      <Chip
        variant='outlined'
        size='small'
        disabled={!bucket?.embedded && !task_id && !loader?.bucket_name}
        color='success'
        sx={{ padding: '3px 2px !important', svg: { marginLeft: `4px !important` } }}
        icon={<FindInPageOutlined sx={{ fontSize: '2ch !important' }} />}
        label='Embedding File'
      />
    </Legend>
  );

  const hasFilesMsg = 'The queue is currently empty.';
  const hasNoFilesMsg = 'The queue is currently empty. When you upload files, you will see them loading here.';
  const isListening = bucket?.embedded || task_id;

  const description = isListening
    ? 'The queue is currently empty and listening for updates to the file embeddings.'
    : !files.length
    ? hasNoFilesMsg
    : hasFilesMsg;

  const isBucketNotEmbedded = !bucket?.embedded && files.length !== 0 && !task_id && !loader?.bucket_name;

  if (data.length === 0 || !data) {
    return (
      <Container>
        <Card
          title='Queue'
          description={description}
          icon={<FactCheckOutlined sx={{ color: theme.primary }} />}
          body={body}
          loading={isQueueFetching && Boolean(bucket?.embedded || task_id)}
        />
        {isBucketNotEmbedded && (
          <Card
            title='Embeddings'
            description='This bucket can only be used by a large language model if it has been embedded first. Go to the "embed" tab to enable embeddings on this bucket.'
            icon={<FindInPageOutlined sx={{ color: '#06a779' }} />}
            actions={
              <Button fullWidth onClick={() => setTab('2')}>
                Embed Tab
              </Button>
            }
          />
        )}
      </Container>
    );
  }

  return (
    <Container>
      <QueueWrapper>
        <FixedSizeList height={listHeight} width='100%' itemSize={46} itemCount={data.length} overscanCount={5}>
          {(props) => {
            const row = data?.[props?.index];
            return <Row row={row} style={props.style} />;
          }}
        </FixedSizeList>
      </QueueWrapper>
    </Container>
  );
};
